import { AlertTriangleIcon, CalendarIcon, EyeIcon, FilterIcon, MapPinIcon, SearchIcon } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { buscarOcorrenciasFiltradas } from '../../service/UserService';
import { relatoriosStyles } from './styles';

function RelatoriosOcorrencias({ onVerDetalhes }) {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [ocorrencias, setOcorrencias] = useState([]);
  const [filtros, setFiltros] = useState({
    dataInicial: '',
    dataFinal: '',
    natureza: '',
    logradouro: '',
    bairro: '',
    apenasNaoAtendidas: false,
    tipoNaoAtendida: ''
  });
  const [showFilters, setShowFilters] = useState(true);

  const [error, setError] = useState(null);

  // Aplicar filtros vindos da URL
  useEffect(() => {
    const filtrosFromURL = {};
    
    // Ler parâmetros da URL
    if (searchParams.get('apenasNaoAtendidas') === 'true') {
      filtrosFromURL.apenasNaoAtendidas = true;
    }
    
    if (searchParams.get('tipoNaoAtendida')) {
      filtrosFromURL.tipoNaoAtendida = searchParams.get('tipoNaoAtendida');
      filtrosFromURL.natureza = searchParams.get('tipoNaoAtendida'); // Para mostrar no campo natureza também
    }
    
    if (searchParams.get('dataInicial')) {
      // Converter de DD/MM/YYYY para YYYY-MM-DD para o input date
      const dataInicial = searchParams.get('dataInicial');
      const [dia, mes, ano] = dataInicial.split('/');
      if (dia && mes && ano) {
        filtrosFromURL.dataInicial = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
      }
    }
    
    if (searchParams.get('dataFinal')) {
      // Converter de DD/MM/YYYY para YYYY-MM-DD para o input date
      const dataFinal = searchParams.get('dataFinal');
      const [dia, mes, ano] = dataFinal.split('/');
      if (dia && mes && ano) {
        filtrosFromURL.dataFinal = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
      }
    }

    // Se há filtros da URL, aplicar e carregar dados
    if (Object.keys(filtrosFromURL).length > 0) {
      setFiltros(prev => ({
        ...prev,
        ...filtrosFromURL
      }));

      // Preparar filtros para a API (manter formato original das datas e incluir todos os campos)
      const filtrosParaAPI = {};
      
      // Campos diretos da URL (já no formato correto)
      if (searchParams.get('apenasNaoAtendidas') === 'true') {
        filtrosParaAPI.apenasNaoAtendidas = true;
      }
      
      if (searchParams.get('tipoNaoAtendida')) {
        filtrosParaAPI.tipoNaoAtendida = searchParams.get('tipoNaoAtendida');
      }
      
      if (searchParams.get('dataInicial')) {
        filtrosParaAPI.dataInicial = searchParams.get('dataInicial');
      }
      
      if (searchParams.get('dataFinal')) {
        filtrosParaAPI.dataFinal = searchParams.get('dataFinal');
      }

      // Outros filtros que possam vir da URL
      ['natureza', 'logradouro', 'bairro'].forEach(campo => {
        if (searchParams.get(campo)) {
          filtrosParaAPI[campo] = searchParams.get(campo);
        }
      });

      // Carregar dados automaticamente
      carregarOcorrencias(filtrosParaAPI);
    }
  }, [searchParams]);

  const carregarOcorrencias = async (filtrosAplicados = {}) => {
    setLoading(true);
    setError(null);
    try {
      const dados = await buscarOcorrenciasFiltradas(filtrosAplicados);
      setOcorrencias(dados);
    } catch (err) {
      setError(err.message);
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar ocorrências',
        text: err.message || 'Não foi possível carregar as ocorrências.',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc2626'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const aplicarFiltros = () => {
    // Preparar filtros para API
    const filtrosParaAPI = {};
    
    // Converter datas de YYYY-MM-DD para DD/MM/YYYY para a API
    if (filtros.dataInicial) {
      const [ano, mes, dia] = filtros.dataInicial.split('-');
      filtrosParaAPI.dataInicial = `${dia}/${mes}/${ano}`;
    }
    
    if (filtros.dataFinal) {
      const [ano, mes, dia] = filtros.dataFinal.split('-');
      filtrosParaAPI.dataFinal = `${dia}/${mes}/${ano}`;
    }
    
    // Incluir todos os outros campos se tiverem valor
    ['natureza', 'logradouro', 'bairro'].forEach(campo => {
      if (filtros[campo] && filtros[campo].trim() !== '') {
        filtrosParaAPI[campo] = filtros[campo];
      }
    });
    
    // Incluir campos específicos de não atendidas
    if (filtros.apenasNaoAtendidas) {
      filtrosParaAPI.apenasNaoAtendidas = true;
    }
    
    if (filtros.tipoNaoAtendida && filtros.tipoNaoAtendida.trim() !== '') {
      filtrosParaAPI.tipoNaoAtendida = filtros.tipoNaoAtendida;
    }
    
    carregarOcorrencias(filtrosParaAPI);
  };

  const limparFiltros = () => {
    const filtrosLimpos = {
      dataInicial: '',
      dataFinal: '',
      natureza: '',
      logradouro: '',
      bairro: '',
      apenasNaoAtendidas: false,
      tipoNaoAtendida: ''
    };
    setFiltros(filtrosLimpos);
    setOcorrencias([]); // Limpar resultados em vez de buscar dados vazios
  };

  const formatarData = (dataString) => {
    if (!dataString) return 'Não informada';
    
    if (dataString.includes('/') && dataString.includes(':')) {
      const [dataParte, horaParte] = dataString.split(' ');
      const [dia, mes, ano] = dataParte.split('/');
      return `${dia}/${mes}/${ano}`;
    }
    
    const dataFormatada = new Date(dataString).toLocaleDateString('pt-BR');
    
    // Se a data é inválida, significa que é uma ocorrência não atendida
    if (dataFormatada === 'Invalid Date' || isNaN(new Date(dataString).getTime())) {
      return 'Não atendida';
    }
    
    return dataFormatada;
  };

  const visualizarOcorrencia = (id) => {
    if (onVerDetalhes) {
      onVerDetalhes(id);
    }
  };

  return (
    <div style={relatoriosStyles.container}>
      <div style={relatoriosStyles.mainContainer}>

        <div style={relatoriosStyles.filterCard}>
          <div style={relatoriosStyles.filterHeader}>
            <h3 style={relatoriosStyles.filterTitle}>
              <FilterIcon style={{ width: '18px', height: '18px', marginRight: '8px' }} />
              Filtros de Busca
            </h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={relatoriosStyles.toggleButton}
            >
              {showFilters ? 'Ocultar' : 'Mostrar'} Filtros
            </button>
          </div>

          {showFilters && (
            <div style={relatoriosStyles.filterContent}>
              <div style={relatoriosStyles.filterGrid}>
                
                <div style={relatoriosStyles.filterField}>
                  <label style={relatoriosStyles.filterLabel}>
                    <CalendarIcon style={{ width: '16px', height: '16px', marginRight: '6px' }} />
                    Data Inicial
                  </label>
                  <input
                    type="date"
                    value={filtros.dataInicial}
                    onChange={(e) => handleFiltroChange('dataInicial', e.target.value)}
                    style={relatoriosStyles.filterInput}
                  />
                </div>

                <div style={relatoriosStyles.filterField}>
                  <label style={relatoriosStyles.filterLabel}>
                    <CalendarIcon style={{ width: '16px', height: '16px', marginRight: '6px' }} />
                    Data Final
                  </label>
                  <input
                    type="date"
                    value={filtros.dataFinal}
                    onChange={(e) => handleFiltroChange('dataFinal', e.target.value)}
                    style={relatoriosStyles.filterInput}
                  />
                </div>

                <div style={relatoriosStyles.filterField}>
                  <label style={relatoriosStyles.filterLabel}>
                    <AlertTriangleIcon style={{ width: '16px', height: '16px', marginRight: '6px' }} />
                    Natureza
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Acidente de Trânsito"
                    value={filtros.natureza}
                    onChange={(e) => handleFiltroChange('natureza', e.target.value)}
                    style={relatoriosStyles.filterInput}
                  />
                </div>

                <div style={relatoriosStyles.filterField}>
                  <label style={relatoriosStyles.filterLabel}>
                    <MapPinIcon style={{ width: '16px', height: '16px', marginRight: '6px' }} />
                    Logradouro
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Av. Principal"
                    value={filtros.logradouro}
                    onChange={(e) => handleFiltroChange('logradouro', e.target.value)}
                    style={relatoriosStyles.filterInput}
                  />
                </div>
                <div style={relatoriosStyles.filterField}>
                  <label style={relatoriosStyles.filterLabel}>
                    <MapPinIcon style={{ width: '16px', height: '16px', marginRight: '6px' }} />
                    Bairro
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Centro"
                    value={filtros.bairro}
                    onChange={(e) => handleFiltroChange('bairro', e.target.value)}
                    style={relatoriosStyles.filterInput}
                  />
                </div>

                <div style={relatoriosStyles.filterField}>
                  <label style={relatoriosStyles.filterLabel}>
                    <AlertTriangleIcon style={{ width: '16px', height: '16px', marginRight: '6px' }} />
                    Apenas Não Atendidas
                  </label>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: '#374151',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={filtros.apenasNaoAtendidas}
                      onChange={(e) => handleFiltroChange('apenasNaoAtendidas', e.target.checked)}
                      style={{
                        width: '16px',
                        height: '16px',
                        accentColor: '#3b82f6'
                      }}
                    />
                    Filtrar apenas ocorrências não atendidas
                  </label>
                </div>

                <div style={relatoriosStyles.filterField}>
                  <label style={relatoriosStyles.filterLabel}>
                    <AlertTriangleIcon style={{ width: '16px', height: '16px', marginRight: '6px' }} />
                    Tipo de Não Atendida
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Sinistro de Trânsito Com Vítima"
                    value={filtros.tipoNaoAtendida}
                    onChange={(e) => handleFiltroChange('tipoNaoAtendida', e.target.value)}
                    style={relatoriosStyles.filterInput}
                    disabled={!filtros.apenasNaoAtendidas}
                  />
                </div>

              </div>

              <div style={relatoriosStyles.filterActions}>
                <button
                  onClick={aplicarFiltros}
                  disabled={loading}
                  style={relatoriosStyles.applyButton}
                >
                  <SearchIcon style={{ width: '16px', height: '16px' }} />
                  {loading ? 'Buscando...' : 'Aplicar Filtros'}
                </button>
                <button
                  onClick={limparFiltros}
                  style={relatoriosStyles.clearButton}
                >
                  Limpar Filtros
                </button>
              </div>
            </div>
          )}
        </div>
        <div style={relatoriosStyles.tableCard}>
          <div style={relatoriosStyles.tableHeader}>
            <h3 style={relatoriosStyles.tableTitle}>
              Resultados ({ocorrencias.length} ocorrências)
            </h3>
          </div>

          {loading ? (
            <div style={relatoriosStyles.loadingContainer}>
              <div style={relatoriosStyles.spinner}></div>
              <p>Carregando ocorrências...</p>
            </div>
          ) : (
            <div style={relatoriosStyles.tableContainer}>
              {ocorrencias.length === 0 ? (
                <div style={relatoriosStyles.emptyState}>
                  <p>
                    {error ? 
                      'Erro ao carregar dados. Tente novamente.' : 
                      'Use os filtros acima para buscar ocorrências específicas ou clique em "Aplicar Filtros" sem preencher nada para ver todas as ocorrências.'
                    }
                  </p>
                </div>
              ) : (
                <table style={relatoriosStyles.table}>
                  <thead>
                    <tr style={relatoriosStyles.tableHeaderRow}>
                      <th style={relatoriosStyles.tableHeaderCell}>Número da Ocorrência</th>
                      <th style={relatoriosStyles.tableHeaderCell}>Data</th>
                      <th style={relatoriosStyles.tableHeaderCell}>Natureza</th>
                      <th style={relatoriosStyles.tableHeaderCell}>Bairro</th>
                      <th style={relatoriosStyles.tableHeaderCell}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ocorrencias.map((ocorrencia) => (
                      <tr key={ocorrencia.id} style={relatoriosStyles.tableRow}>
                        <td style={relatoriosStyles.tableCell}>
                          <span style={relatoriosStyles.numeroOcorrencia}>
                            {ocorrencia.numeroOcorrencia}
                          </span>
                        </td>
                        <td style={relatoriosStyles.tableCell}>
                          {ocorrencia.dataDespacho ? formatarData(ocorrencia.dataDespacho) : 'Não atendida'}
                        </td>
                        <td style={relatoriosStyles.tableCell}>
                          <span style={relatoriosStyles.naturezaBadge}>
                            {ocorrencia.natureza}
                          </span>
                        </td>
                        <td style={relatoriosStyles.tableCell}>
                          {ocorrencia.localizacao.bairro}
                        </td>
                        <td style={relatoriosStyles.tableCell}>
                          <button
                            onClick={() => visualizarOcorrencia(ocorrencia.id)}
                            style={relatoriosStyles.viewButton}
                            title="Visualizar detalhes"
                          >
                            <EyeIcon style={{ width: '16px', height: '16px' }} />
                            Ver Detalhes
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default RelatoriosOcorrencias;
